import {
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
  withStyles,
} from '@material-ui/core';
import React from 'react';

import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { decorate } from '~/src/dash/utils/decorator';
import UploadManager, {
  beautifyUploadProgress,
  EUploadResponseType,
  IUploadInfo,
  IUploadInfoBeautify,
} from '~/src/dash/utils/upload_manager';

export interface IModelSnackBarProgressBarProps {
  uploadManager: UploadManager;
  uploadKey: string;
  uploadName: string;
  isMediafile?: boolean;
  isSavable?: boolean;
}

interface IState {
  infos: IUploadInfoBeautify;
}

const BorderLinearProgress = withStyles({
  root: {
    borderRadius: '2px',
    height: 10,
    backgroundColor: '#bdbdbd',
  },
  bar: {
    borderRadius: 20,
    backgroundColor: getColorTheme('default'), /// ???
  },
})(LinearProgress);

export const ModelSnackBarProgressBarItem = decorate(
  {
    styles: (theme: any) => ({
      icon: {
        fontSize: '1rem',
        marginRight: '1rem',
      },
      error: {
        backgroundColor: theme.palette.error.dark,
      },
      message: {
        display: 'inline-flex',
        'align-items': 'center',
      },
    }),
  },
  class extends React.Component<IModelSnackBarProgressBarProps, IState> {
    state = {
      infos: {
        status: EUploadResponseType.PROGRESS,
        totalSize: '0 MB',
        estimatedTimeLeft: 'Time left',
        uploadedSize: '0 MB',
        progress: 0,
        message: '',
      },
    };

    componentDidMount() {
      const uInfos = this.props.uploadManager.getInfos(this.props.uploadKey);
      if (uInfos !== undefined) {
        this.setState({
          infos: beautifyUploadProgress(uInfos),
        });
      }

      this.props.uploadManager.events.addListener(this.props.uploadKey, (infos: IUploadInfo) => {
        this.setState({
          infos: beautifyUploadProgress(infos),
        });
      });
    }

    generateText(infos: IUploadInfoBeautify): string {
      switch (infos.status) {
        case EUploadResponseType.PROGRESS:
          if (infos.progress === 100) {
            if (this.props.isMediafile && !this.props.isSavable) {
              return 'In progress. You can leave this page and check back later if this is the last/only file to upload.';
            }
            if (this.props.isMediafile && this.props.isSavable) {
              return 'In progress. You can save this line item and check back later.';
            }
          } else {
            return (
              'Uploading file... ' +
              infos.progress +
              '% (' +
              infos.uploadedSize +
              '/' +
              infos.totalSize +
              ')'
            );
          }
        case EUploadResponseType.SUCCESS:
          return 'Upload Successful';
        case EUploadResponseType.FAILURE:
          return infos.message ? 'Upload Error: ' + infos.message : 'Upload Error';
        case EUploadResponseType.ABORTED:
          return 'Upload Cancelled';
      }
      return 'loading infos...';
    }

    generateTextColor(status: EUploadResponseType): string {
      switch (status) {
        case EUploadResponseType.PROGRESS:
          return 'dimgray';
        case EUploadResponseType.SUCCESS:
          return getColorTheme('default'); // ???
        case EUploadResponseType.FAILURE:
          return '#FF5A5F';
      }
      return 'dimgray';
    }

    componentDidUpdate(prevProps: Readonly<IModelSnackBarProgressBarProps>): void {
      if (prevProps.uploadKey !== this.props.uploadKey) {
        const uInfos = this.props.uploadManager.getInfos(this.props.uploadKey);
        if (uInfos !== undefined) {
          this.setState({
            infos: beautifyUploadProgress(uInfos),
          });
        }
      }
    }

    formatName(name: string): string {
      return name.length > 30 ? name.substring(0, 27) + '...' : name;
    }

    render() {
      return (
        <div
          style={{
            height: '95px',
            lineHeight: '2px',
          }}
        >
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item xs container direction={'column'} spacing={0}>
              <Grid item>
                <Typography
                  style={{
                    textOverflow: 'ellipsis',
                    color: getColorTheme('default'),
                  }}
                >
                  {this.formatName(this.props.uploadName)}
                </Typography>
                <Grid container direction='row' alignItems='center' spacing={1}>
                  {this.state.infos.progress === 100 && this.props.isMediafile && (
                    <Grid item xs={2}>
                      <CircularProgress size={20} />
                    </Grid>
                  )}
                  <Grid item xs={10}>
                    <Typography
                      style={{
                        fontWeight: [
                          EUploadResponseType.FAILURE,
                          EUploadResponseType.SUCCESS,
                        ].includes(this.state.infos.status)
                          ? 600
                          : 'normal',
                        fontSize: 12,
                        marginBottom: 3,
                        color: this.generateTextColor(this.state.infos.status),
                      }}
                    >
                      {this.generateText(this.state.infos)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {this.state.infos.status === EUploadResponseType.PROGRESS && (
                <Button
                  onClick={() => {
                    this.props.uploadManager.cancelUpload(this.props.uploadKey);
                  }}
                >
                  Cancel
                </Button>
              )}
            </Grid>
          </Grid>
          <BorderLinearProgress
            variant='determinate'
            color='secondary'
            style={
              [EUploadResponseType.FAILURE, EUploadResponseType.ABORTED].includes(
                this.state.infos.status,
              )
                ? {
                    backgroundColor: 'red',
                  }
                : {}
            }
            value={
              [EUploadResponseType.FAILURE, EUploadResponseType.ABORTED].includes(
                this.state.infos.status,
              )
                ? 0
                : this.state.infos.progress
            }
          />
        </div>
      );
    }
  },
);
